import { registerBrick, QueryResult, ListDef, CloudObject } from 'olympe';
import { ReactBrick, useProperty, cssToSxProps, ifNotTransparent, ifNotNull, useMUITheme, jsonToSxProps } from '@olympeio/core';
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import BCP from '../../common/section_bcp/DM_BCP.js';

import { combineLatestWith, map, switchMap } from 'rxjs/operators';
import { of, combineLatest } from 'rxjs';

import { styled, ThemeProvider } from '@mui/material/styles';
import { Typography, StepLabel, Step, Stepper, Box, StepConnector, Avatar, ListItem, ListItemText, ListItemAvatar, Tooltip } from '@mui/material';
import { stepConnectorClasses } from '@mui/material/StepConnector';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarMonthIcon from '@mui/icons-material/DateRange';
import { getIcon } from '../shared/utils/_icons.jsx';

const StepWithStyling = styled(Step)(({ ownerState }) => ({
    ['.stepper-label--bold .Mui-active']: {
        fontWeight: '700',
    },
    ['.MuiStepLabel-label']: {
        color: ownerState.color || 'black',
        fontFamily: ownerState.fontFamily,
        display: 'flex',
        alignItems: 'center',
    },
    ['.stepper-icon']: {
        fontFamily: ownerState.fontFamily
    },
    ['.inactiveStep .MuiAvatar-root']: {
        backgroundColor: 'grey',
    },
    ['.MuiStepLabel-label span']: {
        color: 'grey',
    },
    ['.rejectedStep .MuiStepLabel-label span']: {
        color: ownerState.rejectedColor,
    }, ['li.MuiListItem-root ']: {
        padding: '0px 15px',
    },
}));
export default class BCPWorkflowStepper extends ReactBrick {

    /**
     * @override
     */
    setupExecution($) {
        // List of properties to observe normally
        const properties = ['Hidden'];
        // Observe the list size to re-trigger an update if necessary
        const observeList = $.observe('List of steps', false);
        const observeListAsArray = observeList.pipe(
            switchMap((list) => {
                let sizeObservable;
                if (Array.isArray(list)) {
                    sizeObservable = of(list.length);
                } else if (list instanceof QueryResult) {
                    sizeObservable = of(list.size());
                } else if (list instanceof ListDef) {
                    sizeObservable = list.observeSize();
                } else {
                    sizeObservable = of(0);
                }
                return sizeObservable.pipe(
                    combineLatestWith($.observe('Reverse', false)),
                    map(([size]) => {
                        const reverse = false;
                        let elements = [];
                        if (size > 0) {
                            // Array case

                            if (Array.isArray(list)) {
                                elements = !reverse ? list : list.reverse();
                            }

                            // QueryResult case
                            else if (list instanceof QueryResult) {
                                elements = !reverse ? list.toArray() : list.toArray().reverse();
                            }

                            // ListDef case
                            else {
                                list.forEachCurrentValue((value, key) => {
                                    elements.push({
                                        value: value,
                                        rank: list.getCurrentRank(key),
                                    });
                                });
                                if (!reverse) {
                                    elements = elements.sort((a, b) => a.rank - b.rank);
                                } else {
                                    elements = elements.sort((a, b) => b.rank - a.rank);
                                }
                                elements = elements.map((e) => e.value);
                            }
                        }
                        return elements;
                    })
                );
            })
        );
        return combineLatest([observeListAsArray, ...properties.map((p) => $.observe(p, false))]);
    }

    /**
     * @override
     */
    static getReactComponent($) {
        const objectToLabels = $.get('Object to label');
        const objectToIcons = $.get('Object to icon');
        const onStepClickProps = $.get('On Step Click');
        const labelAvatar = $.get('Label Avatar');

        const onObjectTolabels = objectToLabels ? [$.runner(objectToLabels), ...objectToLabels.getInputs(), ...objectToLabels.getOutputs()] : null;
        const onLabelAvatar = labelAvatar ? [$.runner(labelAvatar), ...labelAvatar.getInputs(), ...labelAvatar.getOutputs()] : null;

        const onObjectToIcons = objectToIcons ? [$.runner(objectToIcons), ...objectToIcons.getInputs(), ...objectToIcons.getOutputs()] : null;
        const onStepClick = onStepClickProps ? [$.runner(onStepClickProps), ...onStepClickProps.getInputs()] : null;
        return (props) => {
            const [elements, hidden] = props.values;
            if ((elements || []).length === 0 || hidden)
                return (
                    // No list or renderer
                    <Box
                        sx={{
                            backgroundColor: 'lightgrey',
                            width: 1,
                            height: 1,
                            overflow: 'hidden',
                        }}
                    >
                        <Typography sx={{ color: 'black', padding: 1 }}>
                            <b>Stepper</b>
                        </Typography>

                    </Box>
                );

            return <BCPWorkflowStepper.Component $={$} steps={elements} onObjectTolabels={onObjectTolabels} onObjectToIcons={onObjectToIcons} onStepClick={onStepClick} onLabelAvatar={onLabelAvatar} />;
        };
    }
}



BCPWorkflowStepper.Component = ({ $, steps, onObjectTolabels, onObjectToIcons, onStepClick, onLabelAvatar }) => {
    const [activeStep, setActiveStep] = useState(0);
    const boxRef = useRef();
    const width = useProperty($, 'Width');
    const height = useProperty($, 'Height');
    const highlightCurrentStep = useProperty($, 'Highlight selected step');
    const borderRadius = useProperty($, 'Border Radius');
    const borderColor = useProperty($, 'Border Color');
    const borderWidth = useProperty($, 'Border Width');
    const defaultColor = useProperty($, 'Default Color');
    const acceptedColor = useProperty($, 'accepted step color')?.toHexString();
    const rejectedColor = useProperty($, 'rejected step color')?.toHexString();
    const cssProperty = useProperty($, 'Css Property');
    const allowContentOverflow = useProperty($, 'Allow Content Overflow');
    const labelDisplayLocation = useProperty($, 'Label display location');
    const backgroundIconSize = useProperty($, 'Background Icon Size');
    const muiSxJson = useProperty($, 'MUI sx [json]');
    const fontFamily = useProperty($, 'Font Family');
    const textColorOveride = useProperty($, 'Text Color Override');
    let primaryColor = '';
    const bcpRequestData = useProperty($, 'BCP Request');
    useEffect(() => {
        const observeMoveToNextStep = $.observe('Move to next step');
        const observeMoveToPreviousStep = $.observe('Move to previous step');
        const observeCurrentStep = $.observe('Selected step');

        observeCurrentStep.subscribe((currStep) => {
            typeof currStep === 'number' ? setActiveStep(currStep) : setActiveStep(currStep.length - 1);
        });
        observeMoveToNextStep.subscribe((moveToNextStep) => {
            if (moveToNextStep) {
                setActiveStep((prev) => {
                    if (prev >= steps.length - 1) {
                        $.set('Selected step', { ...steps[prev + 1], position: prev });
                    } else {
                        $.set('Selected step', { ...steps[prev + 1], position: prev + 1 });
                    }
                });
            }
        });
        observeMoveToPreviousStep.subscribe((moveToPreviousStep) => {
            if (moveToPreviousStep) {
                setActiveStep((prev) => {
                    if (prev === 0 || prev === undefined) {
                        $.set('Selected step', { ...steps[0], position: 0 });
                    } else {
                        $.set('Selected step', { ...steps[prev - 1], position: prev - 1 > 0 ? prev - 1 : 0 });
                    }
                });
            }
        });
    }, []);

    useLayoutEffect(() => {
        if (boxRef && boxRef.current) {
            boxRef.current.parentElement.style.overflow = allowContentOverflow ? 'unset' : 'hidden';
        }
    }, [boxRef, allowContentOverflow]);

    const setObjectToLabel = (item) => {


        if (onObjectTolabels) {
            const [property$, dataInput, output] = onObjectTolabels;
            property$?.set(dataInput, item);
            primaryColor = property$?.get(output)?.rejectState ? rejectedColor : acceptedColor;

            return property$?.get(output);
        }

        return item?.label;
    };

    const setAvatarIcon = (item) => {
        if (onLabelAvatar) {
            const [property$, dataInput, output] = onLabelAvatar;
            property$?.set(dataInput, item);
            return property$.get(output);
        }
        return item.label;
    };

    const setObjectToIcon = (item) => {
        if (onObjectToIcons) {
            const [property$, dataInput, output] = onObjectToIcons;
            property$?.set(dataInput, item);
            return property$.get(output);
        }
        return item.icon;
    };

    const onClickStep = (step, position) => {
        if (onStepClick) {
            const [onStepClickSignature$, ctrlflowInput, currentStepObj, currentStepRank] = onStepClick;
            onStepClickSignature$.set(currentStepObj, step);
            onStepClickSignature$.set(currentStepRank, position);
            onStepClickSignature$.trigger(ctrlflowInput);
        }
    }

    const CustTooltip = (props) => {
        const state = props.steps.State;
        const requestCreatorName = bcpRequestData ? CloudObject.get(bcpRequestData).get(BCP.requestorProp) : "null";
        const title = props.steps.Assignee;
        const name = title != undefined ? title.split('@')[0].replace('.', ' ').toUpperCase() : 'completed';
        let tooltipTitle;

        if (state === 'waiting validation' || title === 'WC') {
            tooltipTitle = 'waiting validation';
        } else if (state === 'Completed') {
            tooltipTitle = 'Completed';
        } else if (state === "Alternative VS suggestion by BP") {
            tooltipTitle = requestCreatorName;
        } else {
            tooltipTitle = `${name};${title}`;
        }
        return (
            <Tooltip title={tooltipTitle}>
                {props.children}
            </Tooltip>
        );
    }

    const avatarNote = (props) => {
        const state = props.State;
        const title = props.Assignee;
        return ((state == 'Waiting validation' || title == 'WC') ? getIcon('ScheduleIcon') : (state == 'Completed' ? getIcon('DoneIcon') : setAvatarIcon(props)));
    }


    const theme = useMUITheme($);

    return (
        <ThemeProvider theme={theme}>
            <Box ref={boxRef}
                sx={{
                    width: 1,
                    height: 1,
                    boxSizing: 'border-box',
                    // Border and background
                    ...ifNotTransparent('borderColor', borderColor),
                    ...ifNotNull('borderRadius', `${borderRadius}px`, borderRadius),
                    ...ifNotNull('borderWidth', `${borderWidth}px`, borderWidth),
                    ...ifNotTransparent('borderStyle', 'solid', borderColor),
                    ...ifNotTransparent('backgroundColor', defaultColor),

                    '&:hover': {
                        ...ifNotTransparent('borderColor', borderColor),
                        ...ifNotNull('borderRadius', `${borderRadius}px`, borderRadius),
                        ...ifNotNull('borderWidth', `${borderWidth}px`, borderWidth),
                        ...ifNotTransparent('borderStyle', 'solid', borderColor),
                        ...ifNotTransparent('backgroundColor', defaultColor),
                    },
                    // Additional
                    ...cssToSxProps(cssProperty),
                    ...jsonToSxProps(muiSxJson),
                }}>
                <Stepper
                    activeStep={activeStep}
                    alternativeLabel={Boolean(labelDisplayLocation)}
                    connector={<StepConnectorWithStyling ownerState={{ backgroundIconSize }} />}
                    orientation={width >= height ? 'horizontal' : 'vertical'}
                >
                    {steps.map((step, index) => (
                        <StepWithStyling
                            ownerState={{
                                fontFamily,
                                ...ifNotTransparent('color', textColorOveride),
                                rejectedColor
                            }}
                            key={index}
                            onClick={() => {
                                onClickStep(step, index);
                            }}
                        >
                            <StepLabel
                                optional={setObjectToLabel(step).subStep ?

                                    <ListItem sx={{ width: '100%', float: 'right' }}>
                                        <ListItemAvatar sx={{ minWidth: '30px', marginTop: '-15px' }}>
                                            {setObjectToLabel(step).rejectState ? <DescriptionIcon /> : <CalendarMonthIcon />}
                                        </ListItemAvatar>
                                        <ListItemText disableTypography={false} primary={<Typography><b>{setObjectToLabel(step).rejectState ? "Rejection note" : "Target completion date"}</b></Typography>} secondary={setObjectToLabel(step).rejectionNote} />
                                        <ListItemAvatar sx={{ minWidth: '30px', marginTop: '-15spx' }}>
                                            {setObjectToLabel(step).rejectionReasonCommet ? <DescriptionIcon /> : ""}
                                        </ListItemAvatar>
                                        <ListItemText disableTypography={false} primary={<Typography><b>{setObjectToLabel(step).rejectionReasonCommet ? "Rejection Comment" : ""}</b></Typography>} secondary={setObjectToLabel(step)?.rejectionReasonCommet} />
                                    </ListItem> : ''}
                                className={`stepper-label ${highlightCurrentStep ? 'stepper-label--bold' : ''} ${setObjectToLabel(step).rejectState ? 'rejectedStep' : ''} ${(index > activeStep) ? 'inactiveStep' : ''}`}
                                StepIconComponent={(props) => <ColorlibStepIcon {...props} state={step.State} className="stepper-icon" index={index} iconString={setObjectToIcon(step)} $={$} />}
                            >   <CustTooltip steps={step}><Avatar style={{ float: 'left', marginRight: '10px', fontSize: '.9rem' }} sx={{ backgroundColor: primaryColor }}>{avatarNote(step)}</Avatar></CustTooltip>
                                <span sx={{ color: primaryColor }}>{setObjectToLabel(step).label} </span>
                            </StepLabel>
                        </StepWithStyling>
                    ))}
                </Stepper>
            </Box>
        </ThemeProvider>
    );
};

const ColorlibStepIconRoot = styled('div')(({ ownerState }) => ({
    color: ownerState.backgroundIcon ? ownerState.inactiveStepColor : 'transparent',
    position: 'relative',
    zIndex: 1,
    width: ownerState.backgroundIconSize || 40,
    height: ownerState.backgroundIconSize || 40,

    '& .stepper__background-icon-wrap': {
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 0,
    },
    '& .stepper__background-icon-wrap .material-icons': {
        fontSize: ownerState.backgroundIconSize,
    },
    '& .stepper__front-icon': {
        zIndex: 2,
        color: ownerState.frontIconColor,
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
    },
    ...(ownerState.active && {
        color: ownerState.backgroundIcon ? ownerState.selectedStepColor : 'transparent',
    }),
    ...(ownerState.completed && {
        color: ownerState.backgroundIcon ? ownerState.finishedStepColor : 'transparent',
    }),
}));

function ColorlibStepIcon(props) {
    const { active, completed, className, iconString, index, state, $ } = props;
    const backgroundIcon = useProperty($, 'Background Icon') ?? '';

    const inactiveStepColor = useProperty($, 'Inactive steps color')?.toHexString();
    const finishedStepColor = useProperty($, 'Finished steps color')?.toHexString();
    const selectedStepColor = useProperty($, 'Selected step color')?.toHexString();
    const backgroundIconSize = useProperty($, 'Background Icon Size');
    const frontIconColor = useProperty($, 'Front Icon Color')?.toHexString();

    return (
        <ColorlibStepIconRoot ownerState={{
            completed, active, inactiveStepColor, finishedStepColor, selectedStepColor, backgroundIcon, backgroundIconSize, frontIconColor
        }} className={className}>
            {backgroundIcon && <div className='stepper__background-icon-wrap'>
                {getIcon(active ? (state == 'Completed' ? backgroundIcon : 'LensOutlinedIcon') : backgroundIcon)}
            </div>}
            <div className="stepper__front-icon">{iconString ? getIcon(iconString) : index + 1}</div>
        </ColorlibStepIconRoot>
    );

}

const StepConnectorWithStyling = styled(StepConnector)(({ ownerState }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: ownerState.backgroundIconSize / 2,
    },
    [`&.${stepConnectorClasses.vertical}`]: {
        marginLeft: ownerState.backgroundIconSize / 2,
    },
}));


registerBrick('01896dd9efb9496bedd6', BCPWorkflowStepper);
